import React, { createContext, useContext, useState, ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextProps {
  theme: Theme;
  switchTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>('light');

  const switchTheme = (newTheme: Theme) => {
    setTheme(newTheme);
    const root = document.documentElement;
    if (newTheme === 'light') {
      root.style.setProperty('--bg-color', '#ffffff');
      root.style.setProperty('--bg-color-light', '#f5f5f5');
      root.style.setProperty('--bg-color-alt', '#9fe870');
      root.style.setProperty('--bg-color-gray', '#ECEFEB');
      root.style.setProperty('--text-color', '#0E0F0C');
      root.style.setProperty('--text-color-sec', '#454745');
      root.style.setProperty('--text-color-third', '#6A6C6A');
      root.style.setProperty('--text-color-highlight', '#163300');
      root.style.setProperty('--alt-text-color', '#163300');
      root.style.setProperty('--alt-text-color-sec', '#2C6600');
      root.style.setProperty('--button-bg-color', '#9fe870');
      root.style.setProperty('--button-hover-bg-color', '#80e142');
      root.style.setProperty('--button-text-color', '#163300');
      root.style.setProperty('--alt-button-bg-color', '#163300');
      root.style.setProperty('--alt-button-hover-bg-color', '#0d1f00');
      root.style.setProperty('--alt-button-text-color', '#9fe870');
      root.style.setProperty('--menu-text-color', '#163300');
      root.style.setProperty('--menu-hover-text-color', '#0d1f00');
      root.style.setProperty('--link-color', '#163300');
      root.style.setProperty('--alt-link-hover-color', '#0d1f00');
      root.style.setProperty('--negative-color', '#A8200D');
      root.style.setProperty('--positive-color', '#2F5711');
      root.style.setProperty('--warning-color', '#EDC843');
    } else {
      root.style.setProperty('--bg-color', '#163300');
      root.style.setProperty('--bg-color-light', '#1a3d00');
      root.style.setProperty('--bg-color-alt', '#ffffff');
      root.style.setProperty('--bg-color-gray', '#ECEFEB');
      root.style.setProperty('--text-color', '#9fe870');
      root.style.setProperty('--text-color-sec', '#e8ebe6');
      root.style.setProperty('--text-color-third', '#d0d5ce');
      root.style.setProperty('--text-color-highlight', '#ecffe0');
      root.style.setProperty('--alt-text-color', '#0E0F0C');
      root.style.setProperty('--alt-text-color-sec', '#454745');
      root.style.setProperty('--button-bg-color', '#9fe870');
      root.style.setProperty('--button-hover-bg-color', '#cdffad');
      root.style.setProperty('--button-text-color', '#163300');
      root.style.setProperty('--alt-button-bg-color', '#9fe870');
      root.style.setProperty('--alt-button-hover-bg-color', '#80e142');
      root.style.setProperty('--alt-button-text-color', '#163300');
      root.style.setProperty('--menu-text-color', '#9fe870');
      root.style.setProperty('--menu-hover-text-color', '#cdffad');
      root.style.setProperty('--link-color', '#9fe870');
      root.style.setProperty('--alt-link-hover-color', '#0d1f00');
      root.style.setProperty('--negative-color', '#A8200D');
      root.style.setProperty('--positive-color', '#2F5711');
      root.style.setProperty('--warning-color', '#EDC843');
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, switchTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};